// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames as classnames } from "@chbphone55/classnames";
import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import { html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { activateI18n } from "../i18n.js";
import { reset } from "../styles.js";
import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import { wTextfieldStyles } from "./styles/w-textfield.styles.js";
import { styles } from "./styles.js";
import { inputLabelStyles, inputHelpTextStyles } from "./input-styles.js";
// NOTE: Label and help-text are rendered inline using shared input styles.
// In a future major version, we could extract these into separate w-label and w-help-text components
// if we find significant reuse opportunities across non-input components.
const ccinput = {
    // input classes
    base: "block text-m leading-m mb-0 px-8 py-12 rounded-4 w-full focusable focus:[--w-outline-offset:-2px] caret-current", // true
    default: "border-1 s-text s-bg s-border-strong hover:s-border-strong-hover active:s-border-selected", // !isInvalid && !isDisabled && !isReadOnly
    disabled: "border-1 s-text-disabled s-bg-disabled-subtle s-border-disabled pointer-events-none", // !isInvalid && isDisabled && !isReadOnly
    invalid: "border-1 s-text-negative s-bg s-border-negative hover:s-border-negative-hover outline-[--w-s-color-border-negative]!", // isInvalid && !isDisabled && !isReadOnly
    readOnly: "pl-0 bg-transparent pointer-events-none", // !isInvalid && !isDisabled && isReadOnly
    placeholder: "placeholder:s-text-placeholder",
    suffix: "pr-40",
    prefix: "pl-[var(--w-prefix-width,_40px)]",
    // textarea classes
    textArea: "min-h-[42] sm:min-h-[45]",
};
/**
 * A single-line input component used for entering and editing textual or numeric data.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/text-area/frameworks/elements)
 *
 * @slot suffix - Use with `<w-affix>` to include a suffix, for example the unit for a number (e. g. km or sek).
 * @slot prefix - Use with `<w-affix>` to include a prefix, for example a search icon.
 */
class WarpTextField extends FormControlMixin(LitElement) {
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    constructor() {
        super();
        /**
         * Keep in mind that using disabled in its current form is an anti-pattern.
         *
         * There will always be users who don't understand why an element is disabled, or users who can't even see that it is disabled because of poor lighting conditions or other reasons.
         *
         * Please consider more informative alternatives before choosing to use disabled on an element.
         *
         * @summary Makes the element not focusable and hides it from form submits
         */
        this.disabled = false;
        /**
         * Mark the form field as invalid. Make sure to also set a `help-text` to help users fix the validation problem.
         *
         * @summary Mark the form field as invalid.
         */
        this.invalid = false;
        /**
         * Whether to show the optional indicator after the label.
         */
        this.optional = false;
        /**
         * @deprecated Use the native readonly attribute instead.
         */
        this.readOnly = false;
        /**
         * Whether the input can be selected but not changed by the user.
         */
        this.readonly = false;
        /**
         * Whether user input is required on the input before form submission.
         */
        this.required = false;
        /** @internal */
        this._hasPrefix = false;
        /** @internal */
        this._hasSuffix = false;
        this._hasHelpTextSlot = false;
        // capture the initial value using firstUpdated and #initialValue
        this.#initialValue = undefined;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    get #hasHelpText() {
        return typeof this.helpText !== "undefined" || this._hasHelpTextSlot;
    }
    #onKeyDownHandler(e) {
        if (e.key === "Enter" && this.internals.form) {
            this.internals.form.requestSubmit();
        }
    }
    updated(changedProperties) {
        if (changedProperties.has("value") && typeof this.value !== "undefined") {
            this.setValue(this.value);
            if (this.formatter && this.mask) {
                this.mask.innerText = this.formatter(this.value);
            }
        }
    }
    // capture the initial value using firstUpdated and #initialValue
    #initialValue;
    // Note about styling slotted elements:
    // https://developer.mozilla.org/en-US/docs/Web/CSS/::slotted
    // https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_shadow_DOM#styling_slotted_elements
    // Slotted elements remain in lightDOM which allows for control of their style outside of shadowDOM.
    // ::slotted([Simple Selector]) confirms to Specificity rules, but (being simple) does not add weight to lightDOM skin selectors,
    // so never gets higher Specificity. Thus in order to overwrite style linked within shadowDOM, we need to use !important.
    // https://stackoverflow.com/a/61631668
    static { this.styles = [
        reset,
        styles,
        wTextfieldStyles,
        inputLabelStyles,
        inputHelpTextStyles,
    ]; }
    firstUpdated() {
        this.#initialValue = this.value;
    }
    resetFormControl() {
        this.value = this.#initialValue;
    }
    /** @internal */
    get _inputstyles() {
        return classnames([
            ccinput.base,
            this._hasSuffix && ccinput.suffix,
            this._hasPrefix && ccinput.prefix,
            !this.invalid &&
                !this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccinput.default,
            this.invalid &&
                !this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccinput.invalid,
            !this.invalid &&
                this.disabled &&
                !(this.readonly || this.readOnly) &&
                ccinput.disabled,
            !this.invalid &&
                !this.disabled &&
                (this.readonly || this.readOnly) &&
                ccinput.readOnly,
        ]);
    }
    /** @internal */
    get _helptextstyles() {
        return "help-text";
    }
    /** @internal */
    get _label() {
        if (this.label) {
            const showOptionalLabel = this.label.length && this.optional && !this.required;
            const hasTooltip = Boolean(this.tooltip);
            return html `
				<label for="${this._id}">
					${this.label}${showOptionalLabel
                ? html `
									<span>
										${i18n._({
                    id: "textfield.label.optional",
                    message: "Optional",
                    comment: "Shown behind label when marked as optional",
                })}
									</span>
								`
                : nothing}
					${hasTooltip
                ? html `
									<button
										id="tooltip-target"
										class="appearance-none align-text-top bg-transparent m-0 p-0 ml-4"
										part="tooltip-target"
										aria-details="tooltip"
									>
										<w-icon name="Info" size="small"></w-icon>
									</button>
									<w-tooltip for="tooltip-target" id="tooltip">
										${this.tooltip}
									</w-tooltip>
								`
                : nothing}
				</label>
			`;
        }
        return undefined;
    }
    /** @internal */
    get _helpId() {
        if (this.#hasHelpText)
            return `${this._id}__hint`;
        return undefined;
    }
    /** @internal */
    get _id() {
        return "textfield";
    }
    /** @internal */
    get _error() {
        if (this.invalid && this._helpId)
            return this._helpId;
        return undefined;
    }
    handler(e) {
        const { name, value } = e.currentTarget;
        this.value = value;
        // This CustomEvent should be deprecated and removed in the future,
        // as we're essentially firing twice for events. We can't remove
        // it outside of a SemVer major though in case users rely on this
        // instead of the native events.
        // If you're debugging why you get two calls to your event handlers,
        // this is why.
        // To avoid doing work twice check if the event is an instance of CustomEvent
        // and ignore the event:
        //   if (e instanceof CustomEvent) { return; }
        const detail = {
            name,
            value,
            target: e.target,
        };
        const deprecationProxy = new Proxy(detail, {
            get(target, prop) {
                if (typeof window !== "undefined") {
                    // be quiet in prod
                    if (!window.location.host.startsWith("www")) {
                        console.warn("w-textfield's CustomEvent is deprecated, please use the browser-native events instead (e.g. replace e.detail.value with e.target.value)");
                    }
                }
                // @ts-expect-error This is fine
                return target[prop];
            },
        });
        const event = new CustomEvent(e.type, {
            detail: deprecationProxy,
        });
        this.dispatchEvent(event);
    }
    prefixSlotChange() {
        const el = this.renderRoot.querySelector("slot[name=prefix]");
        const affixes = el.assignedElements();
        if (affixes.length)
            this._hasPrefix = true;
    }
    suffixSlotChange() {
        const el = this.renderRoot.querySelector("slot[name=suffix]");
        const affixes = el.assignedElements();
        if (affixes.length)
            this._hasSuffix = true;
    }
    helpTextSlotChange() {
        const el = this.renderRoot.querySelector("slot[name=help-text]");
        const helpText = el.assignedElements();
        if (helpText.length)
            this._hasHelpTextSlot = true;
    }
    render() {
        return html `
			${this._label}
			<div
				class="${classMap({
            "w-textfield": true,
            // This could likely be replaced in the future by
            // https://developer.mozilla.org/en-US/docs/Web/CSS/:has-slotted
            "w-textfield--has-prefix": this._hasPrefix,
            "w-textfield--has-suffix": this._hasSuffix,
        })}"
			>
				<div class="w-textfield__input-wrapper">
					${this.formatter
            ? html `<div class="w-textfield__mask"></div>`
            : nothing}
					<input
						part="input"
						class="${this._inputstyles}"
						type="${this.type || "text"}"
						min="${ifDefined(this.min)}"
						max="${ifDefined(this.max)}"
						size="${ifDefined(this.size)}"
						minlength="${ifDefined(this.minLength || this.minlength)}"
						maxlength="${ifDefined(this.maxLength || this.maxlength)}"
						name="${ifDefined(this.name)}"
						pattern="${ifDefined(this.pattern)}"
						placeholder="${ifDefined(this.placeholder)}"
						.value="${this.value || ""}"
						aria-describedby="${ifDefined(this._helpId ||
            (this.ariaDescription ? "aria-description" : undefined))}"
						aria-errormessage="${ifDefined(this._error)}"
						aria-invalid="${ifDefined(this.invalid)}"
						id="${this._id}"
						?disabled="${this.disabled}"
						?readonly="${this.readonly || this.readOnly}"
						?required="${this.required}"
						autocomplete="${ifDefined(this.autocomplete)}"
						step="${ifDefined(this.step)}"
						@blur="${this.handler}"
						@change="${this.handler}"
						@input="${this.handler}"
						@focus="${this.handler}"
						@keydown="${this.#onKeyDownHandler}"
					/>
				</div>
				<slot @slotchange="${this.prefixSlotChange}" name="prefix"></slot>
				<slot @slotchange="${this.suffixSlotChange}" name="suffix"></slot>
			</div>
			<span class="sr-only" id="aria-description">${this.ariaDescription}</span>
			<div
				?hidden=${!this.#hasHelpText}
				class="${this._helptextstyles}"
				id="${ifDefined(this._helpId)}"
			>
				${this.helpText}
				<slot @slotchange="${this.helpTextSlotChange}" name="help-text"></slot>
			</div>
		`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextField.prototype, "invalid", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: "help-text" })
], WarpTextField.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextField.prototype, "optional", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "size", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpTextField.prototype, "max", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpTextField.prototype, "min", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "min-length" })
], WarpTextField.prototype, "minLength", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpTextField.prototype, "minlength", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "max-length" })
], WarpTextField.prototype, "maxLength", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpTextField.prototype, "maxlength", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "pattern", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "read-only" })
], WarpTextField.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextField.prototype, "readonly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpTextField.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "type", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "value", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "name", void 0);
__decorate([
    property({ type: Number, reflect: true })
], WarpTextField.prototype, "step", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, reflect: true })
], WarpTextField.prototype, "tooltip", void 0);
__decorate([
    property({ attribute: false })
], WarpTextField.prototype, "formatter", void 0);
__decorate([
    query(".w-textfield__mask")
], WarpTextField.prototype, "mask", void 0);
__decorate([
    property({ type: Boolean })
], WarpTextField.prototype, "_hasPrefix", void 0);
__decorate([
    property({ type: Boolean })
], WarpTextField.prototype, "_hasSuffix", void 0);
__decorate([
    state()
], WarpTextField.prototype, "_hasHelpTextSlot", void 0);
if (!customElements.get("w-textfield")) {
    customElements.define("w-textfield", WarpTextField);
}
export { WarpTextField };
//# sourceMappingURL=textfield.js.map